import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card/Card";
import CardContent from "@material-ui/core/CardContent";
import Grid from "@material-ui/core/Grid";
import Link from "@material-ui/core/Link";
import Typography from "@material-ui/core/Typography";
import { IUserAddress } from "@common/eircode/Interface/IAddressForm";
import { FormEvent } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { WORDPRESS_URL } from "@constants";
import { ICustomerDetails } from "@interface/customerDetails/ICustomerDetails";
import { CheckboxWrapper } from "@common/CheckboxWrapper";
import { CustomerDetailsBillingAddress } from "./CustomerDetailsBillingAddress";
import { CustomerDetailsEmail } from "./CustomerDetailsEmail";
import { CustomerDetailsPersonalDetails } from "./CustomerDetailsPersonalDetails";
import { formatUserDate, getCustomerDetailsRequest } from "./utils/customerDetailsUtils";
import { useState, useEffect } from "react";
import { ICart, ICartOffer } from "@features/cart/ICart";
import { keycloakUtils } from "src/itsf/keycloak/keycloakUtils";
import { useFetch } from "@common/hooks/useFetch";
import { CHECK_IF_ID_EXISTS_ROUTE } from "@api-routes";
import { getLanguage } from "@utils/changeLanguage";

export interface ICustomerDetailsForm {
    billingAddress: IUserAddress;
    birthDate: string;
    email: string;
    emailConfirm: string;
    firstName: string;
    isSameDeliveryAddress: boolean;
    keepMeUpdated: boolean;
    lastName: string;
    mobileNumber: string;
    privacyPolicy: boolean;
    precontractualTerms: boolean;
    permissions: string;
    identificationNumber: string;
    communicationLanguage: string;
}

interface IParams {
    userCart?: ICart;
    customerDetails: ICustomerDetails;
    emailError?: string;
    pathName: string;
    setScrollToError(scrollToError: boolean): void;
    updateCustomerDetails(customerDetailsRequest: ICustomerDetails, privacyPolicyValue: boolean): Promise<void>;
}

interface ICheckIdStatus {
    failed: boolean;
    exists: boolean;
}

export const CustomerDetailsForm = (props: IParams) => {
    const { pathName, updateCustomerDetails, setScrollToError, customerDetails, emailError, userCart } = props;
    // const [numberVerified, setNumberVerified] = useState<boolean>(false);
    const [emailVerified, setEmailVerified] = useState<boolean>(false);
    const [postCodeConfirmed, setPostCodeConfirmed] = useState<boolean>(false);
    const { t } = useTranslation(["customerDetails", "common"]);
    const isAuthenticated = keycloakUtils.isAuthenticated();
    const [portInNumbers, setPortInNumbers] = useState<string[]>([]);
    const [privacyLinkClicked, setPrivacyLinkClicked] = useState<boolean>(false);
    const [precontractualTermsClicked, setPrecontractualTermsClicked] = useState<boolean>(false);
    const [checkIdState, { post: postCheckId }] = useFetch();

    const formatPortInNumber = (number: string | undefined): string => {
        let formattedNumber = "";

        if (number) {
            formattedNumber = "357".concat(number);
        }

        return formattedNumber;
    };

    const getDefaultValue = () => {
        if (customerDetails.owner) {
            return {
                billingAddress: {
                    postalCode: customerDetails.billingAddress.postalCode,
                    city: customerDetails.billingAddress.city,
                    area: customerDetails.billingAddress.area,
                    street: customerDetails.billingAddress.street,
                    buildingName: customerDetails.billingAddress.buildingName,
                    buildingType: customerDetails.billingAddress.buildingType,
                    streetQualifier: customerDetails.billingAddress.streetQualifier,
                    streetNumber: customerDetails.billingAddress.streetNumber,
                    postalBox: customerDetails.billingAddress.postalBox,
                    enablePostalBox: customerDetails.billingAddress.enablePostalBox,
                    flatNumber: customerDetails.billingAddress.flatNumber,
                },
                birthDate: formatUserDate(customerDetails.owner.person.birthDate),
                email: customerDetails.owner.email,
                emailConfirm: customerDetails.owner.email,
                firstName: customerDetails.owner.person.firstName,
                lastName: customerDetails.owner.person.lastName,
                mobileNumber: customerDetails.owner.mobileNumber,
                privacyPolicy: false,
                precontractualTerms: false,
                permissions: "",
                identificationNumber: customerDetails.owner.person.identificationNumber,
                communicationLanguage: customerDetails.owner.communicationLanguage,
            };
        }

        return {
            billingAddress: { county: "", enablePostalBox: false },
            birthDate: "",
            deliveryAddress: { county: "" },
            isSameDeliveryAddress: true,
            keepMeUpdated: false,
            privacyPolicy: false,
            precontractualTerms: false,
            permissions: "",
            communicationLanguage: "EL",
            emailConfirm: "",
            email: "",
            mobileNumber: userCart?.offers?.length ? formatPortInNumber(userCart.offers[0].port?.msisdn) : undefined,
        };
    };

    const formMethods = useForm<ICustomerDetailsForm>({
        defaultValues: getDefaultValue(),
    });

    const { trigger, getValues, control, errors, setError, clearErrors } = formMethods;

    const verificationsCompleted = emailVerified && postCodeConfirmed;
    // const verificationsCompleted = numberVerified && emailVerified && postCodeConfirmed;

    useEffect(() => {
        if (customerDetails.owner) {
            // setNumberVerified(true);
            setEmailVerified(true);
            setPostCodeConfirmed(true);
        }

        if (userCart?.offers?.length) {
            let numbers: string[] = [];
            userCart.offers.forEach((offer: ICartOffer) => {
                if (offer.port?.msisdn) numbers.push(formatPortInNumber(offer.port?.msisdn));
            });
            if (numbers.length) {
                setPortInNumbers(numbers);
            }
        }
    }, []);

    const checkIfIdAlreadyExists = async (): Promise<ICheckIdStatus> => {
        let status: ICheckIdStatus = { failed: false, exists: false };

        if (!isAuthenticated)
            await postCheckId<boolean>(CHECK_IF_ID_EXISTS_ROUTE, {
                body: {
                    identityDocumentIdentifier: getValues("identificationNumber"),
                },
            }).then((response) => {
                status = { failed: response !== undefined ? false : true, exists: response || false };
            });

        return status;
    };

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (await trigger()) {
            if (!privacyLinkClicked || !precontractualTermsClicked) {
                !privacyLinkClicked && setError("privacyPolicy", { message: t("customerDetails:tcClickRequired") });
                !precontractualTermsClicked &&
                    setError("precontractualTerms", { message: t("customerDetails:pcClickRequired") });
            } else {
                const checkId: ICheckIdStatus = await checkIfIdAlreadyExists();
                if (!checkId.failed) {
                    if (!checkId.exists) updateDetails();
                    else {
                        setError("identificationNumber", { message: t("customerDetails:idAlreadyExists") });
                        setScrollToError(true);
                    }
                }
            }
        } else {
            setScrollToError(true);
        }
    };

    const updateDetails = () => {
        const formData = getValues();
        const customerDetailsRequest = getCustomerDetailsRequest(formData, customerDetails, isAuthenticated);
        updateCustomerDetails(customerDetailsRequest, formData.privacyPolicy);
    };

    const privacyPolicySentence = (
        <Link
            color="secondary"
            href={`${WORDPRESS_URL}/${getLanguage()}/terms/#8c304bd601c9e87f1`}
            rel="noopener"
            target="_blank"
            underline="always"
            onClick={() => {
                !privacyLinkClicked && setPrivacyLinkClicked(true);
                if (errors.privacyPolicy?.message === t("customerDetails:tcClickRequired")) {
                    clearErrors("privacyPolicy");
                }
            }}
        >
            <b> {t("customerDetails:termsAndConditions")}</b>
        </Link>
    );

    const precontractualTermsSentence = (
        <Link
            color="secondary"
            href={`${WORDPRESS_URL}/${getLanguage()}/terms`}
            rel="noopener"
            target="_blank"
            underline="always"
            onClick={() => {
                !precontractualTermsClicked && setPrecontractualTermsClicked(true);
                if (errors.precontractualTerms?.message === t("customerDetails:pcClickRequired")) {
                    clearErrors("precontractualTerms");
                }
            }}
        >
            <b> {t("customerDetails:precontractualTermsSentence")}</b>
        </Link>
    );

    return (
        <Card className="customer-form" elevation={2}>
            <CardContent>
                <form className="xtest_customer_details_form" onSubmit={onSubmit}>
                    <Grid container direction="column" spacing={4}>
                        <Grid item>
                            <CustomerDetailsEmail
                                emailError={emailError}
                                formMethods={formMethods}
                                pathName={pathName}
                                setEmailVerified={setEmailVerified}
                                emailVerified={emailVerified}
                            />
                        </Grid>
                        <Grid item>
                            <CustomerDetailsPersonalDetails
                                // setNumberVerified={setNumberVerified}
                                // numberVerified={numberVerified}
                                // emailVerified={emailVerified}
                                formMethods={formMethods}
                                portInNumbers={portInNumbers}
                            />
                        </Grid>
                        <Grid item>
                            {!isAuthenticated && (
                                <CustomerDetailsBillingAddress
                                    formMethods={formMethods}
                                    setPostCodeConfirmed={setPostCodeConfirmed}
                                />
                            )}
                        </Grid>
                        <Grid item>
                            <Typography variant="body2">
                                {t("customerDetails:policy")}
                                <Link
                                    color="inherit"
                                    href={`${WORDPRESS_URL}/${getLanguage()}/legal`}
                                    rel="noopener"
                                    target="_blank"
                                    underline="always"
                                >
                                    {t("customerDetails:temrs")}
                                </Link>
                                {t("customerDetails:ours")}
                            </Typography>
                            <CheckboxWrapper
                                color="primary"
                                control={control}
                                error={errors.privacyPolicy}
                                id="policy"
                                isRequired
                                label={privacyPolicySentence}
                                name="privacyPolicy"
                                customRequiredMessage={t("customerDetails:tcRequired")}
                            />
                            <CheckboxWrapper
                                color="primary"
                                control={control}
                                error={errors.precontractualTerms}
                                id="precontractualTerms"
                                isRequired
                                label={precontractualTermsSentence}
                                name="precontractualTerms"
                                customRequiredMessage={t("customerDetails:pcRequired")}
                            />
                            <Grid className="customer-form__buttons" item md={2} sm={3} xs={4}>
                                <Button
                                    className="xtest_confirm_button"
                                    color="primary"
                                    fullWidth
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                    disabled={verificationsCompleted ? false : true}
                                >
                                    {t("common:continue")}
                                </Button>
                            </Grid>
                        </Grid>
                        {checkIdState.error && (
                            <Grid item style={{ float: "right", textAlign: "right" }}>
                                <Typography color="error" variant="body2">
                                    {checkIdState.error.errorMessage}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </form>
            </CardContent>
        </Card>
    );
};
